/**
 * The patient cast (no time travel): each beat the script writes as one patient
 * moving through time is a different patient already at that stage on demo day.
 * Stages are relative to `today`; the SQL seed and the store seed both read this.
 *
 * A cast member may carry its own `orders` (the plan the script states for that
 * patient, which wins over the case-type starter defaults), a `hold` (a
 * pre-admission SOC slot), and `pinnedVisits` (visits the script names by day and
 * time). A pinned visit consumes one plan visit of the same discipline in the
 * same week, so a pin re-times the plan rather than adding to it.
 */
import type { DateKey, Discipline, FrequencyOrder, Payer, PendingDiscipline, VisitTypeKind } from "../state";
import { socDateBefore } from "../engine/clock";

export type CastStage =
  /** 1.2: patient and episode exist; the referral reaches the queue on the event (Alvarez) */
  | "referralPending"
  /** 1.3–1.4: FFS hold waiting on the welcome call (Okafor) */
  | "pendingAdmissionFfs"
  /** 1.3: a further hold on the pending-admissions list (Okonkwo) */
  | "pendingAdmission"
  /** 1.3: hold placed days ago and never converted — the Release example (Jefferson) */
  | "expiredHold"
  /** 2.1–2.2, 3.1–3.3: SOC two days ago, orders synced, plan placed, therapy still pending (Whitfield) */
  | "admittedMonday"
  /** 3.1 / 3.4 / 4.2: MA units cover eight visits; visit nine next week is provisional (Patel) */
  | "pendingAuthNextWeek"
  /** 3.2–3.3: one of Dana's next-working-day patients (Halvorsen, Osei) */
  | "danaThursday"
  /** 5.2: one of Marcus's afternoon visits on the call-out day (Ferreira, Kowalski, Delgado) */
  | "marcusCallOutDay";

/**
 * A visit the script places by hand. `dayOffset` counts **working days** from
 * demo day: 0 is today, 1 the next working day, 2 the one after, −1 the previous
 * working day. A Friday rehearsal therefore reads Friday / Monday / Tuesday and a
 * Wednesday one Wednesday / Thursday / Friday; no pin ever lands on a weekend.
 * `weekOffset` counts demo weeks on top of that (0 = the week of demo day).
 * `everyWeek` repeats the pin on that day's weekday across the seed horizon.
 */
export interface PinnedVisit {
  kind: VisitTypeKind;
  /** Roster handle; defaults to the team's clinician for the visit's discipline. */
  handle?: string;
  /** Working days from demo day (0 = today, 1 = the next working day). */
  dayOffset: number;
  start: string; // "HH:mm" branch-zone wall clock
  weekOffset?: number;
  everyWeek?: boolean;
}

export interface CastHold {
  /** Clinician the slot is held on; absent = unassigned. */
  handle?: string;
  /** Working days from demo day (0 = today, 1 = the next working day), so a hold never lands on a weekend. */
  dayOffset: number;
  windowStart: string;
  windowEnd: string;
}

export interface CastMember {
  ref: string;
  stage: CastStage;
  payer: Payer;
  caseType: string;
  /** Days before today the SOC happened (negative = future). */
  socDaysAgo?: number;
  /** Primary clinicians by discipline (handles). `assessor` is the OASIS-competent RN who does SOC/ROC/recert. */
  team: Partial<Record<"RN" | "LPN" | "PT" | "PTA" | "OT" | "SLP" | "MSW" | "HHA" | "assessor", string>>;
  requiredTraits?: string[];
  /** Orders the script states for this patient; they win over the case type's starter defaults. */
  orders?: FrequencyOrder[];
  /** Disciplines ordered but not yet plotted — a hatched estimate on the branch read. */
  pendingDisciplines?: PendingDiscipline[];
  /** Expected discharge, in working days from demo day (pending admissions). */
  dischargeOffset?: number;
  hold?: CastHold;
  pinnedVisits?: PinnedVisit[];
  /** Real visits only this many days past demo day; later plan visits stay store placeholders. */
  realThroughDays?: number;
  /** Cap on real visits per discipline (MA units): the rest are provisional. */
  realVisitCap?: Partial<Record<Discipline, number>>;
  notes: string;
}

const WOUND_VAC = ["Wound VAC"];

/**
 * Patel's ninth skilled-nursing visit: next week, same weekday, 13:00. The web
 * cast and the phone fixture both derive the date from this rule so the two
 * screens agree without sharing data. Its auth-due
 * reminder is `REAUTH_LEAD_DAYS` (engine/authLedger) before that date.
 */
export const PROVISIONAL_VISIT_OFFSET_DAYS = 7;
export const PROVISIONAL_VISIT_START = "13:00";

/**
 * **The call-out runs on the working day after next** (5.2; discussion, Turns
 * 2–3). Optimize leaves anything already behind the wall clock where it is, so
 * a call-out today breaks at some hour of every working day; the next working
 * day is the text round's, whose confirmed visits Optimize would move. The day
 * after is clear of both, whatever the hour of the demo. Marcus's afternoon is
 * pinned there, Dana's afternoon is light there, and Devon is declared there.
 */
export const CALL_OUT_DAY_OFFSET = 2;

export const CAST: CastMember[] = [
  {
    ref: "alvarez",
    stage: "referralPending",
    payer: "MA",
    caseType: "woundCareSnPt",
    team: { RN: "dana", PT: "ana" },
    requiredTraits: WOUND_VAC,
    dischargeOffset: 1,
    notes: "1.2–1.3: the wound-VAC referral. Discharge the next working day, slips a day during Act 1.",
  },
  {
    ref: "okafor",
    stage: "pendingAdmissionFfs",
    payer: "FFS",
    caseType: "chfSn",
    team: { RN: "marcus", assessor: "marcus" },
    // **One, not zero.** `socDueWindow` is calendar — the 48-hour clock is
    // calendar hours and the branch has a weekend rotation — while the hold's
    // own offset and the generator's `tomorrow` are working days. With a
    // discharge of today, a Friday seed put the SOC window at Fri–Sat, and
    // `clampToSocWindow` pulled his hold off the Monday it asked for and onto
    // **Saturday**, leaving Marcus's Monday morning open for another
    // reservation to take. Discharging tomorrow keeps the window on
    // weekdays on every seed date. The two weekday seeds are unchanged by it.
    dischargeOffset: 1,
    hold: { handle: "marcus", dayOffset: 1, windowStart: "10:00", windowEnd: "12:30" },
    notes: "1.3–1.4: the Medicare FFS pending admission; the welcome call converts the hold to a real SOC on the next working day.",
  },
  {
    ref: "census-10",
    stage: "pendingAdmission",
    payer: "MA",
    caseType: "woundCareSnPt",
    team: { RN: "dana", assessor: "dana", PT: "ana" },
    requiredTraits: WOUND_VAC,
    dischargeOffset: 5,
    hold: { handle: "dana", dayOffset: 5, windowStart: "13:00", windowEnd: "16:00" },
    notes: "1.3: third row on the pending-admissions list; its tail feeds the North SN estimate.",
  },
  {
    ref: "census-05",
    stage: "expiredHold",
    payer: "FFS",
    caseType: "hhaSupport",
    team: { RN: "marcus", assessor: "marcus", HHA: "tasha" },
    dischargeOffset: -4,
    hold: { handle: "marcus", dayOffset: -4, windowStart: "13:00", windowEnd: "16:00" },
    notes: "1.3: held four working days ago, never converted — the Release example.",
  },
  {
    ref: "census-01",
    stage: "admittedMonday",
    payer: "MA",
    caseType: "woundCareSnDecay",
    socDaysAgo: 2,
    team: { RN: "dana", assessor: "dana", LPN: "luis", PT: "ana" },
    requiredTraits: WOUND_VAC,
    pendingDisciplines: [{ discipline: "PT", pointsPerWeek: 2, weeks: 4 }],
    realThroughDays: 14,
    notes: "2.1–2.2, 3.1–3.3: SOC two days before demo day, SN track on Dana, PT eval delivered and PT orders arriving in 2.2.",
  },

  {
    ref: "census-04",
    stage: "pendingAuthNextWeek",
    payer: "MA",
    caseType: "strokeSnPtOtSlp",
    socDaysAgo: 22,
    team: { RN: "dana", assessor: "dana", LPN: "kim", PT: "ana", OT: "rachel", SLP: "jordan" },
    orders: [
      { discipline: "RN", pattern: "2w4, 1w5" },
      { discipline: "PT", pattern: "2w8" },
      { discipline: "OT", pattern: "2w6" },
      { discipline: "SLP", pattern: "1-2w8" },
    ],
    realThroughDays: 14,
    realVisitCap: { RN: 8 },
    notes: "3.1 / 3.4 / 4.2: eight authorized SN visits; visit nine is the provisional one next week, auth due this Friday.",
  },

  {
    ref: "census-30",
    stage: "danaThursday",
    payer: "FFS",
    caseType: "chfSn",
    socDaysAgo: 30,
    team: { RN: "dana", assessor: "dana" },
    orders: [{ discipline: "RN", pattern: "1w9" }],
    realThroughDays: 14,
    pinnedVisits: [{ kind: "SN_ROUTINE", handle: "dana", dayOffset: 1, start: "09:00", everyWeek: true }],
    notes: "3.3: always the next working day at 9:00, the round day's first visit — the Say line leans on her, so she is never the row that does not answer.",
  },
  {
    ref: "osei",
    stage: "danaThursday",
    payer: "FFS",
    caseType: "chfSn",
    socDaysAgo: 23,
    team: { RN: "dana", assessor: "dana" },
    orders: [{ discipline: "RN", pattern: "2w9" }],
    realThroughDays: 14,
    pinnedVisits: [
      { kind: "SN_ROUTINE", handle: "dana", dayOffset: 1, start: "11:00", everyWeek: true },
      // Half past one, not one: the pin is placed before `DANA_WEEK` runs, so it
      // is this time rather than the fixture's that the day actually gets. It
      // moved with the rest of that afternoon so the morning before it is
      // 10:00–13:30 — 210 minutes against the 180 the re-held start of care in
      // 1.3 is sized at. See the note on `DANA_WEEK`'s `dayOffset: 2`.
      { kind: "SN_ROUTINE", handle: "dana", dayOffset: 2, start: "13:30", everyWeek: true },
    ],
    notes: "3.2–3.3: noon on the next working day and half past one the day after; the later visit is the one pushed to next week.",
  },


  {
    ref: "ferreira",
    stage: "marcusCallOutDay",
    payer: "FFS",
    caseType: "woundCareSnPt",
    socDaysAgo: 16,
    team: { RN: "marcus", assessor: "marcus", PT: "ana" },
    requiredTraits: WOUND_VAC,
    orders: [{ discipline: "RN", pattern: "3w2, 2w7", requiredTraits: WOUND_VAC }],
    realThroughDays: 14,
    pinnedVisits: [{ kind: "SN_ROUTINE", handle: "marcus", dayOffset: CALL_OUT_DAY_OFFSET, start: "14:30", everyWeek: true }],
    notes: "5.2 and the Reserve coverage panel: the wound-VAC visit that narrows the pool to Dana and Devon when Marcus calls out.",
  },
  {
    ref: "census-07",
    stage: "marcusCallOutDay",
    payer: "FFS",
    caseType: "chfSn",
    socDaysAgo: 23,
    team: { RN: "marcus", assessor: "marcus" },
    orders: [{ discipline: "RN", pattern: "2w2, 1w7" }],
    realThroughDays: 14,
    pinnedVisits: [{ kind: "SN_ROUTINE", handle: "marcus", dayOffset: CALL_OUT_DAY_OFFSET, start: "13:00", everyWeek: true }],
    notes: "5.2: one of Marcus's afternoon on the call-out day.",
  },
  {
    ref: "census-12",
    stage: "marcusCallOutDay",
    payer: "FFS",
    caseType: "chfSn",
    socDaysAgo: 30,
    team: { RN: "marcus", assessor: "marcus" },
    orders: [{ discipline: "RN", pattern: "1w9" }],
    realThroughDays: 14,
    pinnedVisits: [{ kind: "SN_ROUTINE", handle: "marcus", dayOffset: CALL_OUT_DAY_OFFSET, start: "16:00", everyWeek: true }],
    notes: "5.2: the last of Marcus's afternoon on the call-out day.",
  },


];

export function castByRef(ref: string): CastMember | undefined {
  return CAST.find((c) => c.ref === ref);
}

export function socDateFor(member: CastMember, today: DateKey): DateKey | undefined {
  return member.socDaysAgo === undefined ? undefined : socDateBefore(today, member.socDaysAgo);
}

/** Stages whose patients have no plan visits of their own at seed time. */
export const UNADMITTED_STAGES: CastStage[] = ["referralPending", "pendingAdmissionFfs", "pendingAdmission", "expiredHold"];

export function isUnadmitted(stage: CastStage | undefined): boolean {
  return !!stage && UNADMITTED_STAGES.includes(stage);
}
